import { create } from 'zustand';
import {
  createGeminiNativeImageGeneration,
  createImageEdit,
  createImageGeneration,
  resolveGeminiNativeImageEndpoint,
} from '../api/seedance';
import {
  clipGenerationsToRetentionAndCap,
  extractImageUrlsFromUnknown,
  IMAGE_GEN_CACHE_MAX,
  isOpenAiGptImageModel,
  supportsImageGenGroupMode,
  supportsOpenAiGptImageEdit,
  parseStoredGenerations,
  trimGenerationsForCacheLimit,
  type AspectChoice,
  type GenRecord,
  type GeneratingPlaceholder,
  type ResolutionChoice,
  normalizeReferenceInputsForPersist,
} from '../pages/imageGen/imageGenRecords';
import { IMAGE_GEN_API_KEY, IMAGE_GEN_BASE_URL } from '../utils/seedanceConstants';
import {
  loadImageGenGenerationsJson,
  saveImageGenGenerationsJson,
} from '../utils/seedanceStorage';
import { extractApiMessage } from '../utils/seedanceRequestUtils';
import { stripPromptMentionsForSubmit } from '../utils/seedancePromptUtils';
import { useSeedanceUiStore } from './seedanceUiStore';
import type { ValueUpdater } from './seedanceTypes';
import { resolveValueUpdater } from './seedanceStoreUtils';

export type ImageGenRunParams = {
  promptText: string;
  model: string;
  resolutionUi: ResolutionChoice;
  aspectUi: AspectChoice;
  genMode: 'single' | 'group';
  groupCount: number;
  openAiImageSize: string;
  /** 参考图；Gemini 仅使用第一项，GPT Image 2 组图生图可多张 */
  referenceFiles: { base64: string; mime: string }[];
};

interface SeedanceImageGenState {
  generations: GenRecord[];
  generationsHydrated: boolean;
  /** 当前正在生成的占位（显示 spinner） */
  generatingPlaceholder: GeneratingPlaceholder | null;
  /** 已入队、等待执行的任务参数列表 */
  genQueue: ImageGenRunParams[];
  /** 与 genQueue 平行的占位显示列表（等待中） */
  genQueuePlaceholders: GeneratingPlaceholder[];
  /** true = 队列非空或正在生成 */
  loading: boolean;
  hydrate: () => Promise<void>;
  setGenerations: (
    generations: ValueUpdater<GenRecord[]>,
    options?: { justAppended?: boolean },
  ) => void;
  setGeneratingPlaceholder: (placeholder: GeneratingPlaceholder | null) => void;
  /** 始终可调用：若当前忙则入队，否则立即开始 */
  runGenerate: (params: ImageGenRunParams) => void;
}

async function persistGenerations(list: GenRecord[]) {
  const toSave = clipGenerationsToRetentionAndCap(list);
  await saveImageGenGenerationsJson(JSON.stringify(toSave));
}

function buildPlaceholderFromParams(params: ImageGenRunParams): GeneratingPlaceholder {
  const gptImage = isOpenAiGptImageModel(params.model);
  const groupGenEnabled = supportsImageGenGroupMode(params.model) && params.genMode === 'group';
  let resolutionSend: ResolutionChoice = params.resolutionUi;
  if (groupGenEnabled && resolutionSend === '4K') resolutionSend = '2K';
  const n = groupGenEnabled ? Math.min(4, Math.max(2, params.groupCount)) : 1;
  const id =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `p_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  return {
    id,
    model: params.model,
    prompt: params.promptText.trim(),
    resolution: gptImage ? '1K' : resolutionSend,
    aspect: gptImage ? '1:1' : params.aspectUi,
    createdAt: Date.now(),
    batchCount: n,
    batchParallel: !gptImage && n > 1,
    ...(gptImage ? { imageSize: params.openAiImageSize } : {}),
  };
}

export const useSeedanceImageGenStore = create<SeedanceImageGenState>((set, get) => {
  /** 防止 processQueue 重入 */
  let _processing = false;

  async function doSingleGenerate(params: ImageGenRunParams): Promise<void> {
    const { showToast, addDebug } = useSeedanceUiStore.getState();
    const k = IMAGE_GEN_API_KEY;
    const rawPrompt = params.promptText.trim();
    /** 提交网关：@图N → 图N（与工作台一致） */
    const p = stripPromptMentionsForSubmit(rawPrompt);
    const groupGenEnabled = supportsImageGenGroupMode(params.model) && params.genMode === 'group';
    let resolutionSend: ResolutionChoice = params.resolutionUi;
    if (groupGenEnabled && resolutionSend === '4K') resolutionSend = '2K';
    const gptImage = isOpenAiGptImageModel(params.model);
    const n = groupGenEnabled ? Math.min(4, Math.max(2, params.groupCount)) : 1;
    const baseUrl = IMAGE_GEN_BASE_URL;

    const refAssets = params.referenceFiles.filter(
      (x) =>
        typeof x.base64 === 'string' &&
        x.base64.replace(/\s/g, '').length > 0 &&
        typeof x.mime === 'string',
    );
    const persistedRefInputs = normalizeReferenceInputsForPersist(refAssets);
    const recordRefExtras = persistedRefInputs?.length
      ? { referenceInputs: persistedRefInputs }
      : {};
    const recordGenExtras = {
      genMode: (groupGenEnabled ? 'group' : 'single') as 'single' | 'group',
      ...(groupGenEnabled ? { groupCount: n } : {}),
    };

    const appendFailureRecord = (errMsgRaw: string) => {
      const trimmed = errMsgRaw.trim() || '请求失败';
      const safeErr =
        trimmed.length > 480 ? `${trimmed.slice(0, 477)}…` : trimmed;
      const id =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `fail_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      get().setGenerations(
        (prev) => [
          {
            id,
            model: params.model,
            prompt: rawPrompt,
            images: [],
            createdAt: Date.now(),
            resolution: gptImage ? '1K' : resolutionSend,
            aspect: gptImage ? '1:1' : params.aspectUi,
            ...(gptImage ? { imageSize: params.openAiImageSize } : {}),
            generationError:
              n > 1 ? `组图 ${n} 张均未生成：${safeErr}` : safeErr,
            ...recordRefExtras,
            ...recordGenExtras,
          },
          ...prev,
        ],
        { justAppended: true },
      );
    };

    try {
      let imageUrls: string[] = [];
      let failCount = 0;
      let gptBatchError: unknown | undefined;
      let geminiSettled: PromiseSettledResult<unknown>[] | undefined;

      if (gptImage) {
        if (refAssets.length && !supportsOpenAiGptImageEdit(params.model)) {
          showToast('GPT Image 1 不支持根据图片生图，已忽略参考图', 'i');
        }
        const gptUseEdits =
          refAssets.length > 0 && supportsOpenAiGptImageEdit(params.model);
        addDebug(
          gptUseEdits
            ? `POST ${baseUrl}/v1/images/edits（multipart，图生图 model=${params.model}，参考文件 ${refAssets.length} 个，n=${n}，size=${params.openAiImageSize}）`
            : `POST ${baseUrl}/v1/images/generations（OpenAI 文生图，model=${params.model}，n=${n}，size=${params.openAiImageSize}，单次请求）`,
        );
        const settledGpt = await Promise.allSettled([
          gptUseEdits
            ? createImageEdit(
                {
                  model: params.model,
                  prompt: p,
                  imageRefs: refAssets,
                  n,
                  size: params.openAiImageSize,
                },
                { baseUrl, apiKey: k },
              )
            : createImageGeneration(
                {
                  model: params.model,
                  prompt: p,
                  n,
                  size: params.openAiImageSize,
                },
                { baseUrl, apiKey: k },
              ),
        ]);
        const item = settledGpt[0];
        if (item.status === 'rejected') {
          gptBatchError = item.reason;
          failCount = n;
          const r = item.reason as { data?: unknown; message?: unknown };
          const errMsg =
            extractApiMessage(r) ||
            extractApiMessage(r?.data) ||
            String(r?.message ?? item.reason) ||
            '请求失败';
          addDebug(`GPT Image 生成失败: ${errMsg}`, true);
        } else {
          const urls = extractImageUrlsFromUnknown(item.value);
          imageUrls = urls.slice(0, n);
          if (!imageUrls.length) {
            failCount = n;
            addDebug('单次请求未解析到图片，请检查网关返回结构', true);
          } else if (imageUrls.length < n) {
            failCount = n - imageUrls.length;
            addDebug(`单次请求仅返回 ${imageUrls.length}/${n} 张`, true);
          }
        }
      } else {
        addDebug(
          `POST ${baseUrl}${resolveGeminiNativeImageEndpoint(params.model)} × ${n}（并行，每次 number_of_images=1）`,
        );
        const payloadBase: Parameters<typeof createGeminiNativeImageGeneration>[0] = {
          model: params.model,
          prompt: p,
          resolution: resolutionSend,
          number_of_images: 1,
          aspect_ratio: params.aspectUi,
          ...(refAssets.length
            ? {
                reference_image_base64: refAssets[0].base64.replace(/\s/g, ''),
                reference_mime_type: refAssets[0].mime.trim(),
              }
            : {}),
        };

        geminiSettled = await Promise.allSettled(
          Array.from({ length: n }, () =>
            createGeminiNativeImageGeneration(payloadBase, {
              baseUrl,
              apiKey: k,
            }),
          ),
        );

        for (let i = 0; i < geminiSettled.length; i++) {
          const item = geminiSettled[i];
          if (item.status === 'rejected') {
            failCount += 1;
            const r = item.reason as { data?: unknown; message?: unknown };
            const errMsg =
              extractApiMessage(r) ||
              extractApiMessage(r?.data) ||
              String(r?.message ?? item.reason) ||
              '请求失败';
            addDebug(`第 ${i + 1} 张生成失败: ${errMsg}`, true);
            continue;
          }
          const urls = extractImageUrlsFromUnknown(item.value);
          if (!urls.length) {
            failCount += 1;
            addDebug(`第 ${i + 1} 次请求未解析到图片，请检查网关返回结构`, true);
            continue;
          }
          imageUrls.push(urls[0]);
        }
      }

      if (imageUrls.length === 0) {
        const errMsg = gptImage
          ? gptBatchError
            ? extractApiMessage(gptBatchError) ||
              extractApiMessage((gptBatchError as { data?: unknown })?.data) ||
              String((gptBatchError as Error)?.message ?? gptBatchError).trim() ||
              '请求失败'
            : '全部请求未返回有效图片'
          : (() => {
              const firstRejected = geminiSettled?.find(
                (s): s is PromiseRejectedResult => s.status === 'rejected',
              );
              return firstRejected
                ? extractApiMessage(firstRejected.reason) ||
                    extractApiMessage((firstRejected.reason as { data?: unknown })?.data) ||
                    String((firstRejected.reason as Error)?.message ?? firstRejected.reason).trim() ||
                    '请求失败'
                : '全部请求未返回有效图片';
            })();
        addDebug(`图像生成失败: ${errMsg}`, true);
        showToast(n > 1 ? `组图全部失败（${n} 张）${errMsg ? `：${errMsg}` : ''}` : errMsg, 'e');
        appendFailureRecord(errMsg);
        return;
      }

      const id =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `g_${Date.now()}`;
      get().setGenerations(
        (prev) => [
          {
            id,
            model: params.model,
            prompt: rawPrompt,
            images: imageUrls,
            createdAt: Date.now(),
            resolution: gptImage ? '1K' : resolutionSend,
            aspect: gptImage ? '1:1' : params.aspectUi,
            ...(gptImage ? { imageSize: params.openAiImageSize } : {}),
            ...recordRefExtras,
            ...recordGenExtras,
          },
          ...prev,
        ],
        { justAppended: true },
      );

      if (failCount > 0) {
        showToast(
          n > 1 ? `已生成 ${imageUrls.length}/${n} 张，${failCount} 张失败` : '生成完成（部分失败）',
          'i',
        );
      } else {
        showToast(n > 1 ? `生成完成（${n} 张）` : '生成完成', 's');
      }
    } catch (err: unknown) {
      const anyErr = err as { data?: unknown; message?: string };
      const errMsg =
        extractApiMessage(anyErr) ||
        extractApiMessage(anyErr?.data) ||
        String(anyErr?.message || err) ||
        '请求失败';
      addDebug(`图像生成失败: ${errMsg}`, true);
      useSeedanceUiStore.getState().showToast(errMsg, 'e');
      appendFailureRecord(errMsg);
    }
  }

  async function processQueue(): Promise<void> {
    if (_processing) return;
    _processing = true;
    try {
      while (true) {
        const state = get();
        if (state.genQueue.length === 0) break;

        const [params, ...restQueue] = state.genQueue;
        const [currentPlaceholder, ...restPlaceholders] = state.genQueuePlaceholders;
        set({
          genQueue: restQueue,
          genQueuePlaceholders: restPlaceholders,
          generatingPlaceholder: currentPlaceholder ?? null,
        });

        await doSingleGenerate(params);
      }
    } finally {
      _processing = false;
      set({ loading: false, generatingPlaceholder: null });
    }
  }

  return {
    generations: [],
    generationsHydrated: false,
    generatingPlaceholder: null,
    genQueue: [],
    genQueuePlaceholders: [],
    loading: false,

    hydrate: async () => {
      if (get().generationsHydrated) return;
      const { showToast } = useSeedanceUiStore.getState();
      try {
        const raw = await loadImageGenGenerationsJson();
        set({
          generations: clipGenerationsToRetentionAndCap(parseStoredGenerations(raw)),
          generationsHydrated: true,
        });
      } catch {
        showToast('读取本地文生图历史失败，已以空白开始', 'e');
        set({ generationsHydrated: true });
      }
    },

    setGenerations: (generations, options) => {
      set((state) => {
        const raw = resolveValueUpdater(state.generations, generations);
        const { list: next, evictedByCap } = trimGenerationsForCacheLimit(raw, {
          justAppended: options?.justAppended,
        });
        if (evictedByCap > 0) {
          useSeedanceUiStore.getState().showToast(
            `本地生成记录接近存储上限（${IMAGE_GEN_CACHE_MAX} 条），已自动清理 ${evictedByCap} 条最早记录`,
            'i',
          );
        }
        void persistGenerations(next).catch(() => {
          useSeedanceUiStore
            .getState()
            .showToast('本地缓存写入失败，可减少历史条数或清理磁盘空间', 'e');
        });
        return { generations: next };
      });
    },

    setGeneratingPlaceholder: (generatingPlaceholder) => set({ generatingPlaceholder }),

    runGenerate: (params) => {
      const { showToast } = useSeedanceUiStore.getState();
      const p = params.promptText.trim();
      if (!p) {
        showToast('请输入创意描述', 'e');
        return;
      }

      const placeholder = buildPlaceholderFromParams(params);
      const currentState = get();
      const alreadyQueued = currentState.genQueue.length + (currentState.loading ? 1 : 0);

      set((s) => ({
        genQueue: [...s.genQueue, params],
        genQueuePlaceholders: [...s.genQueuePlaceholders, placeholder],
        loading: true,
      }));

      if (alreadyQueued > 0) {
        showToast(`已加入队列（当前第 ${alreadyQueued + 1} 个）`, 'i');
      }

      void processQueue();
    },
  };
});
